import { TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

/**
 * NumericField — a controlled MUI TextField for numeric input that avoids the
 * React "012" bug caused by type="number" browser sanitization.
 *
 * Key behaviours:
 *  - Maintains its own display-string state so intermediate edits (e.g. "1.") are preserved.
 *  - Auto-selects all text on focus — no need to add onFocus separately.
 *  - Calls onChange(number) only when the user has entered a valid finite number.
 *  - On blur, reverts display to the last valid parent value when input is empty or invalid.
 *  - Passes all standard MUI TextField props through unchanged.
 *
 * @param {number|string} value     - Controlled value from parent.
 * @param {function}      onChange  - Called with a plain Number when value is valid.
 *                                    Signature: onChange(num: number) — NOT (event).
 * @param {object}        inputProps - Forwarded to the underlying <input> (min/max/step etc.).
 */
export default function NumericField({ value, onChange, inputProps = {}, onFocus, onBlur, ...props }) {
    const toDisplay = (v) => (v != null && v !== '' ? String(v) : '');

    const [display, setDisplay] = useState(() => toDisplay(value));
    const focused = useRef(false);

    // Sync from parent only while the field is NOT focused (avoid clobbering mid-edit).
    useEffect(() => {
        if (!focused.current) {
            setDisplay(toDisplay(value));
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const raw = e.target.value;
        setDisplay(raw);
        const num = Number(raw);
        // Only propagate when the raw string is a finished, valid number (not "1." or "").
        if (raw !== '' && raw !== '-' && !raw.endsWith('.') && Number.isFinite(num)) {
            onChange?.(num);
        }
    };

    const handleFocus = (e) => {
        focused.current = true;
        e.target.select();
        onFocus?.(e);
    };

    const handleBlur = (e) => {
        focused.current = false;
        const num = Number(display);
        if (display === '' || !Number.isFinite(num)) {
            // Revert display to last valid parent value — don't silently zero-out.
            setDisplay(toDisplay(value));
        } else {
            // Normalize trailing dot / leading zeros ("1." → "1", "01" → "1").
            setDisplay(String(num));
            // If the normalized number differs from what was propagated, fire onChange now.
            onChange?.(num);
        }
        onBlur?.(e);
    };

    return (
        <TextField
            {...props}
            type="text"
            inputProps={{ inputMode: 'decimal', ...inputProps }}
            value={display}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
        />
    );
}
